import { BookingAddressPicker } from '@components/Address/BookingAddressPicker';
import { NavioContactChatWidget } from '@components/BookingFlow/Common/NavioContactChatWidget';
import { ContentWrapperCustomerApp } from '@components/Layouts/ContentWrapperCustomerApp';
import { FlowBackButton } from '@components/Layouts/FlowBackButton';
import { TireOfferDeliveryCheckInfo } from '@components/TireOffer/TireOfferDeliveryCheckInfo';
import { useTireOfferDeliveryCheck } from '@components/TireOffer/useTireOfferDeliveryCheck';
import { t } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import { TireQuoteRecordCustomer } from '@navio/nsdk';
import { ApiErrorPageInternal, FixedBottomBarContainer, NoddiButton, NoddiInformationBox } from 'noddi-ui-web';
import { customerRoutes } from 'noddi-util';
import { useNavigate } from 'react-router-dom';
import { useTireOfferStore } from '../../../stores/tireOfferStore';

interface AddressPickerWithDeliveryCheckProps {
  tireOffer: TireQuoteRecordCustomer;
}

export const AddressPickerWithDeliveryCheck = ({ tireOffer }: AddressPickerWithDeliveryCheckProps) => {
  const { selectedAddress, setSelectedAddress } = useTireOfferStore();
  const navigate = useNavigate();

  const {
    data: deliveryCheck,
    isPending: isDeliveryCheckPending,
    error: deliveryCheckError
  } = useTireOfferDeliveryCheck({ tireOffer, addressId: selectedAddress?.id });

  const hasDeliveryCheck = !!selectedAddress && !!deliveryCheck;

  return (
    <ContentWrapperCustomerApp title={t`Where do you need our help?`} hideBackButton>
      <div className='flex flex-col gap-2'>
        {!hasDeliveryCheck && (
          <NoddiInformationBox size='small' title={t`Why do we ask about your address?`}>
            <p>
              {
                // eslint-disable-next-line noddi/no-banned-ui-terms -- consumer copy: end customers read 'technician'; 'worker' is the backoffice term
                t`Since our technicians come to your location - we need your address to verify that we can deliver tire mounting services in your area.`
              }
            </p>
          </NoddiInformationBox>
        )}
        <BookingAddressPicker
          hideServiceAreaCheck
          setSelectedAddress={setSelectedAddress}
          selectedAddress={selectedAddress}
        />
        {deliveryCheckError ? <ApiErrorPageInternal error={deliveryCheckError} /> : null}
        {hasDeliveryCheck && <TireOfferDeliveryCheckInfo deliversToAddress={deliveryCheck.deliversToAddress} />}
      </div>
      <FixedBottomBarContainer contentWidth='md'>
        <div className='flex w-full items-center justify-between gap-2'>
          <FlowBackButton />
          {/* Right of the action on phones, beside Back from md. */}
          <NavioContactChatWidget size='small' className='max-md:order-last' />
          <NoddiButton
            className='ml-auto'
            disabled={!selectedAddress || isDeliveryCheckPending || deliveryCheck?.deliversToAddress === false}
            endIcon='ArrowRight'
            // oxlint-disable-next-line typescript/no-misused-promises -- async onClick runs navigate(); React discards the returned promise, so a rejection is unhandled unless the body catches
            onClick={() =>
              navigate(
                customerRoutes.tireOfferSummary.getPath({
                  slug: tireOffer.slug
                })
              )
            }
          >
            <Trans>Next</Trans>
          </NoddiButton>
        </div>
      </FixedBottomBarContainer>
    </ContentWrapperCustomerApp>
  );
};
